import { FormEvent, useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  DEAL_LOST_REASON_LABELS,
  DEAL_LOST_REASON_VALUES,
  type DealLostReason,
} from '@marketing-hub/shared';
import { apiFetch } from '@/api/client';
import type { Deal, Pipeline, PipelineBoardStage, PipelineForecast } from '@/api/types';
import { useProjectContext } from '@/project/ProjectContext';
import { ProjectGate, ProjectSelectionHint } from '@/features/projects/ProjectGate';
import { ActivityComposer } from '@/features/activities/ActivityComposer';
import { ActivityTimeline } from '@/features/activities/ActivityTimeline';
import { Button } from '@/components/ui/Button';
import { Field, TextArea, TextInput } from '@/components/ui/Field';
import { ErrorState, LoadingState, StateMessage } from '@/components/StateMessage/StateMessage';
import projectStyles from './ProjectsPage.module.css';
import styles from './PipelinePage.module.css';

function formatMoney(amount: number, currency: string): string {
  return new Intl.NumberFormat('nl-NL', { style: 'currency', currency }).format(amount);
}

export function PipelinePage() {
  const { selectedProjectId } = useProjectContext();
  const qc = useQueryClient();
  const [view, setView] = useState<'kanban' | 'list'>('kanban');
  const [pipelineId, setPipelineId] = useState<string | null>(null);
  const [selectedDealId, setSelectedDealId] = useState<string | null>(null);
  const [dragDealId, setDragDealId] = useState<string | null>(null);
  const [dropStageId, setDropStageId] = useState<string | null>(null);
  const [quickName, setQuickName] = useState('');
  const [quickAmount, setQuickAmount] = useState('');

  const pipelinesQ = useQuery({
    queryKey: ['pipelines', selectedProjectId],
    enabled: Boolean(selectedProjectId),
    queryFn: () =>
      apiFetch<{ pipelines: Pipeline[] }>(`/api/pipelines?project_id=${selectedProjectId}`),
  });

  const activePipelineId = useMemo(() => {
    if (pipelineId) return pipelineId;
    const list = pipelinesQ.data?.pipelines ?? [];
    return list.find((p) => p.is_default)?.id ?? list[0]?.id ?? null;
  }, [pipelineId, pipelinesQ.data?.pipelines]);

  const boardQ = useQuery({
    queryKey: ['pipeline-board', selectedProjectId, activePipelineId],
    enabled: Boolean(selectedProjectId && activePipelineId),
    queryFn: () =>
      apiFetch<{ pipeline: Pipeline; stages: PipelineBoardStage[] }>(
        `/api/pipelines/${activePipelineId}/board?project_id=${selectedProjectId}`,
      ),
  });

  const forecastQ = useQuery({
    queryKey: ['pipeline-forecast', selectedProjectId, activePipelineId],
    enabled: Boolean(selectedProjectId && activePipelineId),
    queryFn: () =>
      apiFetch<{ forecast: PipelineForecast }>(
        `/api/pipelines/forecast?project_id=${selectedProjectId}&pipeline_id=${activePipelineId}`,
      ),
  });

  const dealQ = useQuery({
    queryKey: ['deal', selectedDealId],
    enabled: Boolean(selectedDealId),
    queryFn: () => apiFetch<{ deal: Deal }>(`/api/deals/${selectedDealId}`),
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['pipeline-board'] });
    void qc.invalidateQueries({ queryKey: ['pipeline-forecast'] });
    void qc.invalidateQueries({ queryKey: ['deal'] });
  };

  const moveDeal = useMutation({
    mutationFn: ({
      dealId,
      stageId,
      lostReason,
    }: {
      dealId: string;
      stageId: string;
      lostReason?: DealLostReason;
    }) =>
      apiFetch(`/api/deals/${dealId}`, {
        method: 'PATCH',
        body: JSON.stringify({ stage_id: stageId, lost_reason: lostReason ?? null }),
      }),
    onSuccess: () => invalidate(),
  });

  const createDeal = useMutation({
    mutationFn: (body: {
      name: string;
      stage_id: string;
      amount: number;
      primary_lead_id?: string | null;
      account_id?: string | null;
    }) =>
      apiFetch('/api/deals', {
        method: 'POST',
        body: JSON.stringify({
          project_id: selectedProjectId,
          pipeline_id: activePipelineId,
          ...body,
        }),
      }),
    onSuccess: () => {
      setQuickName('');
      setQuickAmount('');
      invalidate();
    },
  });

  const patchDeal = useMutation({
    mutationFn: ({ id, ...patch }: Partial<Deal> & { id: string }) =>
      apiFetch(`/api/deals/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),
    onSuccess: () => invalidate(),
  });

  const firstOpenStage = boardQ.data?.stages.find((s) => s.outcome === 'open');

  function submitQuick(e: FormEvent) {
    e.preventDefault();
    if (!firstOpenStage || !quickName.trim()) return;
    createDeal.mutate({
      name: quickName.trim(),
      stage_id: firstOpenStage.id,
      amount: Number(quickAmount) || 0,
    });
  }

  const allDeals = useMemo(
    () =>
      boardQ.data?.stages.flatMap((s) => s.deals.map((d) => ({ ...d, stageName: s.name }))) ?? [],
    [boardQ.data?.stages],
  );

  return (
    <ProjectGate pageTitle="Pipeline">
      <h1 className={styles.title}>Pipeline</h1>
      <ProjectSelectionHint />
      <StateMessage title="CRM C">
        Kanban per project, gewogen forecast en deal-detail. Sleep deals tussen kolommen.
      </StateMessage>

      {pipelinesQ.isLoading || boardQ.isLoading ? (
        <LoadingState />
      ) : pipelinesQ.isError ? (
        <ErrorState message={(pipelinesQ.error as Error).message} />
      ) : !activePipelineId ? (
        <StateMessage title="Geen pipeline">Migratie nog niet uitgevoerd?</StateMessage>
      ) : (
        <>
          <div className={styles.toolbar}>
            <select
              className={projectStyles.filterInput}
              value={activePipelineId}
              onChange={(e) => setPipelineId(e.target.value)}
            >
              {(pipelinesQ.data?.pipelines ?? []).map((p) => (
                <option key={p.id} value={p.id}>
                  {p.name}
                </option>
              ))}
            </select>
            <Button
              variant={view === 'kanban' ? 'primary' : 'secondary'}
              onClick={() => setView('kanban')}
            >
              Kanban
            </Button>
            <Button
              variant={view === 'list' ? 'primary' : 'secondary'}
              onClick={() => setView('list')}
            >
              Lijst
            </Button>
          </div>

          {forecastQ.data ? (
            <div className={styles.summary}>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Open pipeline</div>
                <div className={styles.summaryValue}>
                  {formatMoney(forecastQ.data.forecast.openPipeline, 'EUR')}
                </div>
                <div className={styles.columnMeta}>{forecastQ.data.forecast.openCount} deals</div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Gewogen forecast</div>
                <div className={styles.summaryValue}>
                  {formatMoney(forecastQ.data.forecast.weightedPipeline, 'EUR')}
                </div>
              </div>
              <div className={styles.summaryCard}>
                <div className={styles.summaryLabel}>Gewonnen</div>
                <div className={styles.summaryValue}>
                  {formatMoney(forecastQ.data.forecast.wonTotal, 'EUR')}
                </div>
                <div className={styles.columnMeta}>{forecastQ.data.forecast.wonCount} deals</div>
              </div>
            </div>
          ) : null}

          {firstOpenStage ? (
            <form className={projectStyles.card} onSubmit={(e) => void submitQuick(e)}>
              <strong>Snelle deal</strong> → {firstOpenStage.name}
              <div className={projectStyles.row}>
                <TextInput
                  placeholder="Dealnaam"
                  value={quickName}
                  onChange={(e) => setQuickName(e.target.value)}
                  required
                />
                <TextInput
                  placeholder="Bedrag"
                  type="number"
                  min={0}
                  step="0.01"
                  value={quickAmount}
                  onChange={(e) => setQuickAmount(e.target.value)}
                />
                <Button type="submit" disabled={createDeal.isPending}>
                  Toevoegen
                </Button>
              </div>
            </form>
          ) : null}

          {view === 'kanban' ? (
            <div className={styles.kanban}>
              {(boardQ.data?.stages ?? []).map((stage) => (
                <div
                  key={stage.id}
                  className={[styles.column, dropStageId === stage.id ? styles.columnDrop : '']
                    .filter(Boolean)
                    .join(' ')}
                  ref={(el) => {
                    if (el) el.style.setProperty('--stage-color', stage.color);
                  }}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDropStageId(stage.id);
                  }}
                  onDragLeave={() => setDropStageId(null)}
                  onDrop={() => {
                    if (!dragDealId) return;
                    const deal = allDeals.find((d) => d.id === dragDealId);
                    if (deal?.stage_id === stage.id) {
                      setDragDealId(null);
                      setDropStageId(null);
                      return;
                    }
                    if (stage.outcome === 'lost') {
                      const reason = window.prompt(
                        'Verliesreden (price, timing, competitor, no_budget, no_fit, ghosted, other):',
                        'other',
                      ) as DealLostReason | null;
                      if (!reason || !DEAL_LOST_REASON_VALUES.includes(reason)) {
                        setDragDealId(null);
                        setDropStageId(null);
                        return;
                      }
                      moveDeal.mutate({
                        dealId: dragDealId,
                        stageId: stage.id,
                        lostReason: reason,
                      });
                    } else {
                      moveDeal.mutate({ dealId: dragDealId, stageId: stage.id });
                    }
                    setDragDealId(null);
                    setDropStageId(null);
                  }}
                >
                  <div className={styles.columnHeader}>
                    <strong>{stage.name}</strong>
                    <div className={styles.columnMeta}>
                      {stage.deal_count} · {formatMoney(stage.total_amount, 'EUR')} ·{' '}
                      {stage.win_probability}%
                    </div>
                  </div>
                  <div className={styles.columnBody}>
                    {stage.deals.map((deal) => (
                      <button
                        type="button"
                        key={deal.id}
                        className={[styles.dealCard, deal.is_stale ? styles.dealCardStale : '']
                          .filter(Boolean)
                          .join(' ')}
                        draggable
                        onDragStart={() => setDragDealId(deal.id)}
                        onClick={() => setSelectedDealId(deal.id)}
                      >
                        <div>{deal.name}</div>
                        <div className={styles.dealAmount}>
                          {formatMoney(deal.amount, deal.currency)}
                        </div>
                        {deal.account_name || deal.lead_label ? (
                          <div className={styles.dealSub}>
                            {[deal.account_name, deal.lead_label].filter(Boolean).join(' · ')}
                          </div>
                        ) : null}
                        {deal.is_stale ? (
                          <div className={styles.dealSub}>Stagnatie — verplaats of follow-up</div>
                        ) : null}
                      </button>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <table className={styles.listTable}>
              <thead>
                <tr>
                  <th>Deal</th>
                  <th>Stage</th>
                  <th>Bedrag</th>
                  <th>Account</th>
                </tr>
              </thead>
              <tbody>
                {allDeals.map((d) => (
                  <tr
                    key={d.id}
                    className={styles.listRowClick}
                    onClick={() => setSelectedDealId(d.id)}
                  >
                    <td>{d.name}</td>
                    <td>{d.stageName}</td>
                    <td>{formatMoney(d.amount, d.currency)}</td>
                    <td>{d.account_name ?? '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {selectedDealId && dealQ.data?.deal ? (
            <DealEditor
              deal={dealQ.data.deal}
              stages={boardQ.data?.stages ?? []}
              onClose={() => setSelectedDealId(null)}
              onSave={(patch) => patchDeal.mutate({ id: selectedDealId, ...patch })}
              onDelete={async () => {
                await apiFetch(`/api/deals/${selectedDealId}`, { method: 'DELETE', body: '{}' });
                setSelectedDealId(null);
                invalidate();
              }}
            />
          ) : null}
        </>
      )}
    </ProjectGate>
  );
}

function DealEditor({
  deal,
  stages,
  onClose,
  onSave,
  onDelete,
}: {
  deal: Deal;
  stages: PipelineBoardStage[];
  onClose: () => void;
  onSave: (patch: Partial<Deal>) => void;
  onDelete: () => void;
}) {
  const [name, setName] = useState(deal.name);
  const [amount, setAmount] = useState(String(deal.amount));
  const [stageId, setStageId] = useState(deal.stage_id);
  const [closeDate, setCloseDate] = useState(deal.expected_close_date ?? '');
  const [lostReason, setLostReason] = useState(deal.lost_reason ?? '');
  const [competitor, setCompetitor] = useState(deal.competitor ?? '');
  const [notes, setNotes] = useState(deal.notes ?? '');

  const targetStage = stages.find((s) => s.id === stageId);

  return (
    <section className={styles.panel}>
      <div className={projectStyles.row}>
        <h2>Deal</h2>
        <Button variant="ghost" onClick={onClose}>
          Sluiten
        </Button>
      </div>
      <div className={styles.formGrid}>
        <Field label="Naam">
          <TextInput value={name} onChange={(e) => setName(e.target.value)} />
        </Field>
        <Field label="Bedrag (EUR)">
          <TextInput
            type="number"
            min={0}
            step="0.01"
            value={amount}
            onChange={(e) => setAmount(e.target.value)}
          />
        </Field>
        <Field label="Stage">
          <select
            className={projectStyles.filterInput}
            value={stageId}
            onChange={(e) => setStageId(e.target.value)}
          >
            {stages.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Verwachte sluitdatum">
          <TextInput type="date" value={closeDate} onChange={(e) => setCloseDate(e.target.value)} />
        </Field>
        {targetStage?.outcome === 'lost' ? (
          <>
            <Field label="Verliesreden">
              <select
                className={projectStyles.filterInput}
                value={lostReason}
                onChange={(e) => setLostReason(e.target.value)}
              >
                <option value="">—</option>
                {DEAL_LOST_REASON_VALUES.map((r) => (
                  <option key={r} value={r}>
                    {DEAL_LOST_REASON_LABELS[r]}
                  </option>
                ))}
              </select>
            </Field>
            <Field label="Concurrent">
              <TextInput value={competitor} onChange={(e) => setCompetitor(e.target.value)} />
            </Field>
          </>
        ) : null}
        <Field label="Notities">
          <TextArea rows={3} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Field>
      </div>
      <div className={projectStyles.row}>
        <Button
          onClick={() =>
            onSave({
              name,
              amount: Number(amount) || 0,
              stage_id: stageId,
              expected_close_date: closeDate || null,
              lost_reason: (lostReason || null) as DealLostReason | null,
              competitor: competitor || null,
              notes: notes || null,
            })
          }
        >
          Opslaan
        </Button>
        <Button
          variant="danger"
          onClick={() => {
            if (window.confirm('Deal definitief verwijderen?')) onDelete();
          }}
        >
          Verwijderen
        </Button>
      </div>
      {deal.lead_label ? <p className={styles.dealSub}>Contact: {deal.lead_label}</p> : null}
      <h3>Activiteiten</h3>
      <ActivityComposer projectId={deal.project_id} dealId={deal.id} />
      <ActivityTimeline projectId={deal.project_id} dealId={deal.id} />
    </section>
  );
}
